/**
 * DocumentPreviewDrawer — side drawer with an embedded document preview
 * (Brief 91 + bundle-builder reliability follow-up).
 *
 * Fetches an ORDERED LIST of candidate preview URLs from
 * POST /api/documents/preview on open (best tier first — Graph /preview
 * embed, then downloadUrl, then PDF web viewer, then bare webUrl). Renders
 * the first tier in an iframe; if that iframe fails to load (onError, or a
 * load timeout because some SharePoint failures render a same-origin error
 * page without ever firing onError), it silently advances to the next tier.
 *
 * Why: Graph's /preview getUrl frequently 404s inside SharePoint's own
 * embed.aspx viewer even when Graph returned 200 for it — a failure that
 * only ever shows up once the iframe tries to load, so the backend can't
 * pre-empt it. Falling through tiers client-side means a flaky tier 1 never
 * strands the user on a dead "File Not Found" page.
 *
 * BM-H3887: before any SharePoint tier, the drawer asks the backend for the
 * document rendered as a PDF by the Bundle Builder's own converter
 * (POST /api/documents/preview/rendered) and shows that. SharePoint cannot
 * show a saved email inline at all (its embed refuses .eml/.msg and the
 * download URL makes the browser save the file), so emails previewed as a
 * blank pane. The rendered page is also exactly what the bundle will carry,
 * which is the point of checking it for privileged material before adding
 * it. If that call fails (an older backend, a file the converter cannot
 * read) the SharePoint tiers below take over as before.
 *
 * Closes on Esc or backdrop click.
 */
import { useEffect } from "react";
import { X, ExternalLink } from "lucide-react";
import DocumentPreviewBody from "./DocumentPreviewBody";

// BM-H4266: the preview itself (tiers, iframe, fallbacks) moved to
// DocumentPreviewBody so the matter page's documents pane shows documents
// the same way. This drawer is the chrome around it. ``strategy`` is
// DocumentPreviewBody's: "bundle" (default, Finder and Bundle Builder) or
// "matter" (File Snap rows, the matter page).
export default function DocumentPreviewDrawer({ doc, onClose, strategy = "bundle" }) {
  const open = !!doc;

  // Esc closes the drawer.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="doc-preview-drawer">
      <div
        className="absolute inset-0 bg-black/50"
        data-testid="doc-preview-backdrop"
        onClick={() => onClose && onClose()}
      />
      <div className="relative w-full max-w-2xl h-full bg-navy-card border-l border-navy-border shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-navy-border">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-white truncate" title={doc.title}>
              {doc.title || "Preview"}
            </h2>
            {doc.matter_ref && (
              <div className="text-xs text-teal truncate">{doc.matter_ref}</div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {doc.web_url && (
              <a
                href={doc.web_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="doc-preview-open"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs
                  bg-slate-800 text-slate-200 border border-navy-border hover:bg-slate-700"
              >
                <ExternalLink size={13} /> Open
              </a>
            )}
            <button
              type="button"
              data-testid="doc-preview-close"
              onClick={() => onClose && onClose()}
              className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-700"
              aria-label="Close preview"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <DocumentPreviewBody
          doc={doc}
          strategy={strategy}
          renderedNote={strategy === "bundle" ? "Shown as it will appear in the bundle." : undefined}
        />
      </div>
    </div>
  );
}
